import React, { useCallback, useEffect, useState } from 'react';
import barcodeInventoryService from '../services/barcodeInventoryService';
import masterService from '../services/masterService';
import './BarcodeInventoryPage.css';

const EMPTY_FORM = { prefix: '', fromNo: '', toNo: '', numberWidth: 5, centreId: '', remarks: '' };
const MAX_RANGE_SIZE = 1000000;

/** Client-side mirror of the server rules (the server remains the authority). Returns error strings. */
export const validateRangeForm = (f) => {
  const errors = [];
  const prefix = (f.prefix || '').trim().toUpperCase();
  const from = Number(f.fromNo);
  const to = Number(f.toNo);
  const width = Number(f.numberWidth);
  if (!/^[A-Z]{1,5}$/.test(prefix)) errors.push('Prefix must be 1 to 5 letters (A-Z).');
  if (!Number.isInteger(width) || width < 1 || width > 10) errors.push('Width must be between 1 and 10 digits.');
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < from) {
    errors.push("'To' must be greater than or equal to 'From'.");
  } else {
    if (Number.isInteger(width) && width >= 1 && width <= 10 && to >= 10 ** width) errors.push(`'To' does not fit in ${width} digits.`);
    if (to - from + 1 > MAX_RANGE_SIZE) errors.push(`A single range cannot exceed ${MAX_RANGE_SIZE.toLocaleString()} barcodes.`);
  }
  if (prefix.length + width > 15) errors.push('Prefix plus width cannot exceed 15 characters.');
  return errors;
};

const toPayload = (f) => ({
  prefix: (f.prefix || '').trim().toUpperCase(),
  fromNo: Number(f.fromNo),
  toNo: Number(f.toNo),
  numberWidth: Number(f.numberWidth),
  centreId: f.centreId === '' ? null : Number(f.centreId),
  remarks: f.remarks?.trim() || null,
});

const errorText = (err, fallback) => {
  const data = err.response?.data;
  const details = Array.isArray(data?.errors) ? data.errors.filter((e) => e !== data.message) : [];
  return [data?.message || fallback, ...details].join(' ');
};

export const BarcodeInventoryPage = () => {
  const [ranges, setRanges] = useState({ items: [], totalCount: 0, page: 1, pageSize: 10 });
  const [page, setPage] = useState(1);
  const [centres, setCentres] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [lookupValue, setLookupValue] = useState('');
  const [lookup, setLookup] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  const loadRanges = useCallback(async (p) => {
    try {
      const res = await barcodeInventoryService.listRanges(p, 10);
      if (res.success) setRanges(res.data);
      else setMessage({ type: 'error', text: res.message || 'Failed to load barcode ranges.' });
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'Failed to load barcode ranges.') });
    }
  }, []);

  useEffect(() => {
    loadRanges(page);
  }, [page, loadRanges]);

  useEffect(() => {
    masterService
      .getCentres({ isActive: true })
      .then((res) => res.success && setCentres(res.data || []))
      .catch(() => {});
  }, []);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setPreview(null); // any edit invalidates the previous preview
    setMessage(null);
  };

  const formErrors = validateRangeForm(form);

  const handlePreview = async () => {
    if (formErrors.length || busy) return;
    setBusy(true);
    try {
      const res = await barcodeInventoryService.previewRange(toPayload(form));
      setPreview(res.data);
    } catch (err) {
      setPreview(null);
      setMessage({ type: 'error', text: errorText(err, 'Preview failed.') });
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!preview?.isValid || busy) return;
    setBusy(true);
    try {
      const res = await barcodeInventoryService.createRange(toPayload(form));
      if (res.success) {
        setMessage({ type: 'success', text: `Range created: ${preview.first} to ${preview.last} (${preview.count.toLocaleString()} barcodes).` });
        setForm(EMPTY_FORM);
        setPreview(null);
        setPage(1);
        await loadRanges(1);
      } else {
        setMessage({ type: 'error', text: res.message || 'Could not create the range.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'Could not create the range.') });
    } finally {
      setBusy(false);
    }
  };

  const handleVoidRange = async (range) => {
    const reason = window.prompt(`Void range ${range.first} to ${range.last}? Unassigned numbers will become unusable.\nReason:`);
    if (!reason || !reason.trim()) return;
    try {
      await barcodeInventoryService.voidRange(range.rangeId, reason.trim());
      setMessage({ type: 'success', text: `Range ${range.first} to ${range.last} voided.` });
      await loadRanges(page);
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'Could not void the range.') });
    }
  };

  const handleLookup = async (e) => {
    e?.preventDefault();
    const value = lookupValue.trim();
    if (!value || busy) return;
    setBusy(true);
    setLookup(null);
    try {
      const res = await barcodeInventoryService.lookup(value);
      setLookup(res.data);
      setVoidReason('');
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'Lookup failed.') });
    } finally {
      setBusy(false);
    }
  };

  const canVoid = lookup && (lookup.status === 'Available' || lookup.status === 'Assigned') && lookup.source === 'PREPRINTED';

  const handleVoidBarcode = async () => {
    if (!canVoid || !voidReason.trim()) return;
    try {
      await barcodeInventoryService.voidBarcode(lookup.barcode, voidReason.trim());
      setMessage({ type: 'success', text: `Barcode ${lookup.barcode} voided.` });
      const res = await barcodeInventoryService.lookup(lookup.barcode);
      setLookup(res.data);
      await loadRanges(page);
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'Could not void the barcode.') });
    }
  };

  const totalPages = Math.max(1, Math.ceil(ranges.totalCount / 10));

  return (
    <div className="bcinv-container">
      <header className="bcinv-header">
        <h1>Pre-printed Barcode Inventory</h1>
        <p>
          Ranges of printed stickers (letters + digits, for example AAA00001). A barcode can be assigned once; used, assigned and
          voided numbers can never be assigned again.
        </p>
      </header>

      {message && (
        <div className={`bcinv-msg bcinv-msg--${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>
          {message.text}
        </div>
      )}

      <section className="bcinv-card" aria-label="Create range">
        <h2>New range</h2>
        <div className="bcinv-form">
          <label>Prefix<input aria-label="Prefix" value={form.prefix} maxLength={5} onChange={(e) => setField('prefix', e.target.value.toUpperCase())} /></label>
          <label>From<input aria-label="From" type="number" min="0" value={form.fromNo} onChange={(e) => setField('fromNo', e.target.value)} /></label>
          <label>To<input aria-label="To" type="number" min="0" value={form.toNo} onChange={(e) => setField('toNo', e.target.value)} /></label>
          <label>Digits<input aria-label="Digits" type="number" min="1" max="10" value={form.numberWidth} onChange={(e) => setField('numberWidth', e.target.value)} /></label>
          <label>
            Centre
            <select aria-label="Centre" value={form.centreId} onChange={(e) => setField('centreId', e.target.value)}>
              <option value="">All centres</option>
              {centres.map((c) => (
                <option key={c.centreId} value={c.centreId}>{c.centreName}</option>
              ))}
            </select>
          </label>
          <label className="bcinv-wide">Remarks<input aria-label="Remarks" value={form.remarks} maxLength={200} onChange={(e) => setField('remarks', e.target.value)} /></label>
        </div>

        {form.prefix && formErrors.length > 0 && (
          <ul className="bcinv-errors">{formErrors.map((e) => <li key={e}>{e}</li>)}</ul>
        )}

        <div className="bcinv-actions">
          <button type="button" onClick={handlePreview} disabled={busy || formErrors.length > 0}>Preview</button>
          <button type="button" className="bcinv-primary" onClick={handleCreate} disabled={busy || !preview?.isValid}>Create range</button>
        </div>

        {preview && (
          <div className={`bcinv-preview ${preview.isValid ? 'is-valid' : 'is-invalid'}`} role="status">
            {preview.isValid ? (
              <span>{preview.count.toLocaleString()} barcodes: <strong>{preview.first}</strong> to <strong>{preview.last}</strong></span>
            ) : (
              <ul>{preview.errors.map((e) => <li key={e}>{e}</li>)}</ul>
            )}
            {preview.overlaps?.length > 0 && (
              <ul>
                {preview.overlaps.map((o) => (
                  <li key={o.rangeId}>Overlaps range #{o.rangeId}: {o.first} to {o.last}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className="bcinv-card" aria-label="Ranges">
        <h2>Ranges</h2>
        <div className="bcinv-table-wrap">
          <table className="bcinv-table">
            <thead>
              <tr><th>#</th><th>Range</th><th>Centre</th><th>Total</th><th>Available</th><th>Assigned</th><th>Used</th><th>Void</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {ranges.items.length === 0 && (
                <tr><td colSpan={10} className="bcinv-empty">No ranges yet.</td></tr>
              )}
              {ranges.items.map((r) => (
                <tr key={r.rangeId}>
                  <td>{r.rangeId}</td>
                  <td>{r.first} to {r.last}</td>
                  <td>{r.centreId ?? 'All'}</td>
                  <td>{r.total.toLocaleString()}</td>
                  <td>{r.available.toLocaleString()}</td>
                  <td>{r.assigned.toLocaleString()}</td>
                  <td>{r.used.toLocaleString()}</td>
                  <td>{r.voided.toLocaleString()}</td>
                  <td><span className={`bcinv-badge bcinv-badge--${r.status.toLowerCase()}`}>{r.status}</span></td>
                  <td>{r.status === 'Active' && <button type="button" className="bcinv-link" onClick={() => handleVoidRange(r)}>Void</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bcinv-pager">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </section>

      <section className="bcinv-card" aria-label="Barcode lookup">
        <h2>Scan / look up a barcode</h2>
        <form className="bcinv-lookup" onSubmit={handleLookup}>
          <input aria-label="Barcode" placeholder="Scan or type barcode" value={lookupValue} onChange={(e) => setLookupValue(e.target.value)} autoComplete="off" />
          <button type="submit" disabled={busy || !lookupValue.trim()}>Look up</button>
        </form>

        {lookup && (
          <div className="bcinv-result" role="region" aria-label="Lookup result">
            {!lookup.found ? (
              <p>Barcode <strong>{lookup.barcode}</strong> is not in the inventory and has no order lines.</p>
            ) : (
              <>
                <p><strong>{lookup.barcode}</strong> · {lookup.source} · <span className="bcinv-status">{lookup.status}</span>
                  {lookup.ledgerTransactionNo ? ` · order ${lookup.ledgerTransactionNo}` : ''}</p>
                {lookup.voidReason && <p>Void reason: {lookup.voidReason}</p>}
                {lookup.lines?.length > 0 && (
                  <ul>
                    {lookup.lines.map((l) => (
                      <li key={l.testId}>{l.ledgerTransactionNo} · {l.itemName} · status {l.collectionStatus}</li>
                    ))}
                  </ul>
                )}
                {lookup.events?.length > 0 && (
                  <ol className="bcinv-events">
                    {lookup.events.map((ev, i) => (
                      <li key={i}>{new Date(ev.eventDate).toLocaleString()} · {ev.eventType}{ev.detail ? ` · ${ev.detail}` : ''}{ev.actor ? ` (${ev.actor})` : ''}</li>
                    ))}
                  </ol>
                )}
                {canVoid && (
                  <div className="bcinv-void">
                    <input aria-label="Void reason" placeholder="Reason (required)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
                    <button type="button" onClick={handleVoidBarcode} disabled={!voidReason.trim()}>Void barcode</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default BarcodeInventoryPage;
